import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { staffChipClass, staffInputClass } from '../components/layout/staffPageStyles';
import {
  ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, FileText, Info, Loader2, RefreshCw, TrendingDown,
} from 'lucide-react';
import { openTicketPdf } from '../utils/ticket80mmPrint';

/**
 * « Pertes » (la direction) — ventes hors stock sous le prix catalogue et remises excessives,
 * par vendeur et en détail. Calculé côté backend (/admin/pertes), export CSV.
 * Chaque ligne permet d’ouvrir le ticket de caisse (PDF, nouvel onglet) et d’afficher
 * en ligne le contenu de la vente (articles, paiement, total).
 */

const PAGE_SIZE = 50;
const REMISE_SEUIL_PCT = 20;

// Les clés sont des codes backend (non traduits) ; le libellé affiché passe par i18n (labelKey).
const TYPE_META = {
  hors_stock_sous_prix: { labelKey: 'adminPertes.typeHorsStock', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  remise_excessive: { labelKey: 'adminPertes.typeRemise', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  prix_sous_catalogue: { labelKey: 'adminPertes.typeSousCatalogue', cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30' },
};

const PRESETS = [
  { key: '30j', labelKey: 'adminPertes.preset30j', days: 30 },
  { key: '3m', labelKey: 'adminPertes.preset3m', days: 91 },
  { key: '12m', labelKey: 'adminPertes.preset12m', days: 365 },
  { key: 'custom', labelKey: 'adminPertes.presetCustom', days: null },
];

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const asArray = (v) => (Array.isArray(v) ? v : []);

const isoDate = (d) => {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const daysAgo = (n) => isoDate(Date.now() - n * 86400000);

// Codes backend du mode de paiement -> clé i18n (valeurs traduisibles) ; repli sur la valeur brute.
const MODE_PAIEMENT_KEYS = {
  cb: 'adminPertes.payCb', carte: 'adminPertes.payCb', card: 'adminPertes.payCb', carte_bancaire: 'adminPertes.payCb',
  especes: 'adminPertes.payCash', 'espèces': 'adminPertes.payCash', cash: 'adminPertes.payCash',
  cheque: 'adminPertes.payCheque', 'chèque': 'adminPertes.payCheque',
  virement: 'adminPertes.payVirement', mixte: 'adminPertes.payMixte',
  avoir: 'adminPertes.payAvoir', acompte: 'adminPertes.payAcompte',
};
// Marques : non traduites.
const MODE_PAIEMENT_BRANDS = { sumup: 'SumUp', paypal: 'PayPal' };

/** Total d’une ligne d’article : prix_total du backend, sinon qté × prix unitaire. */
const ligneTotal = (a) => {
  if (a?.prix_total !== null && a?.prix_total !== undefined && a?.prix_total !== '') return num(a.prix_total);
  if (a?.prix_unitaire === null || a?.prix_unitaire === undefined || a?.prix_unitaire === '') return null;
  return (num(a.quantite) || 1) * num(a.prix_unitaire);
};

const statutClass = (s) => {
  const v = String(s || '').toLowerCase();
  if (['confirmee', 'confirmée', 'confirmed', 'validee', 'validée', 'paid', 'payee', 'payée'].includes(v)) return 'text-green-400';
  if (['pending', 'en_attente', 'attente', 'a_confirmer', 'à confirmer'].includes(v)) return 'text-amber-400';
  if (['rejetee', 'rejetée', 'rejected', 'annulee', 'annulée', 'cancelled'].includes(v)) return 'text-red-400';
  return 'text-gray-300';
};

const Tile = ({ label, value, accent, hint, testId }) => (
  <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid={testId}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-2xl font-black tabular-nums mt-1 ${accent || 'text-white'}`}>{value}</div>
    {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
  </div>
);

export default function AdminPertesPage() {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate, formatNumber } = useLanguage();
  const [preset, setPreset] = useState('30j');
  const [dateDebut, setDateDebut] = useState(daysAgo(30));
  const [dateFin, setDateFin] = useState(isoDate(Date.now()));
  const [vendeur, setVendeur] = useState('');
  const [type, setType] = useState('');
  const [magasinId, setMagasinId] = useState('');
  const [page, setPage] = useState(0);
  const [sortDir, setSortDir] = useState('desc'); // tri sur la perte (colonne principale)

  const [rows, setRows] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [periode, setPeriode] = useState(null);
  const [computedAt, setComputedAt] = useState('');
  const [note, setNote] = useState('');
  const [totalRows, setTotalRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [vendeursConnus, setVendeursConnus] = useState([]);
  const [magasinsConnus, setMagasinsConnus] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set()); // lignes dont le détail de la vente est déplié
  const [ticketBusy, setTicketBusy] = useState(''); // ref_vente dont le ticket PDF est en cours d’ouverture
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Helpers de formatage localisés (remplacent les anciens Intl 'fr-FR' / '€').
  const nf = (x) => formatNumber(x, { maximumFractionDigits: 2 });
  const eur = (v) => (v === null || v === undefined || v === '' ? '—' : formatCurrency(num(v)));
  const pct = (v) => (v === null || v === undefined || v === '' ? '—' : formatNumber(num(v) / 100, { style: 'percent', maximumFractionDigits: 2 }));
  const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const fmtDateTime = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const seuilPct = formatNumber(REMISE_SEUIL_PCT / 100, { style: 'percent' });
  const plural = (base, n) => t(`adminPertes.${base}_${Number(n) > 1 ? 'other' : 'one'}`, { count: nf(n) });

  const modePaiementLabel = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    const k = String(v).toLowerCase().trim();
    if (MODE_PAIEMENT_KEYS[k]) return t(MODE_PAIEMENT_KEYS[k]);
    if (MODE_PAIEMENT_BRANDS[k]) return MODE_PAIEMENT_BRANDS[k];
    return String(v);
  };

  /** Info-bulle compacte sur la référence ticket : les 3 premières désignations vendues. */
  const ticketTooltip = (r) => {
    const arts = asArray(r?.articles);
    if (!arts.length) return r?.ref_vente ? t('adminPertes.ticketTooltipRef', { ref: r.ref_vente }) : '';
    const names = arts.slice(0, 3).map((a) => a?.designation).filter(Boolean);
    const extra = arts.length - 3;
    const more = extra > 0 ? ` ${plural('autres', extra)}` : '';
    return `${names.join(' · ')}${more}`;
  };

  const TypeBadge = ({ type: typeCode }) => {
    const meta = TYPE_META[typeCode];
    const label = meta ? t(meta.labelKey) : (typeCode || '—');
    const cls = meta ? meta.cls : 'bg-white/5 text-gray-300 border-white/10';
    return <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap ${cls}`}>{label}</span>;
  };

  const applyPreset = (key) => {
    setPreset(key);
    setPage(0);
    const p = PRESETS.find((x) => x.key === key);
    if (p && p.days) { setDateDebut(daysAgo(p.days)); setDateFin(isoDate(Date.now())); }
  };

  const params = useMemo(() => {
    const q = { date_debut: dateDebut, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (dateFin) q.date_fin = dateFin;
    if (vendeur) q.vendeur = vendeur;
    if (type) q.type = type;
    if (magasinId) q.magasin_id = magasinId;
    return q;
  }, [dateDebut, dateFin, vendeur, type, magasinId, page]);

  const load = useCallback(async ({ refresh = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/pertes', { params: refresh ? { ...params, refresh: 1 } : params });
      if (!mountedRef.current) return;
      const d = res.data || {};
      const r = asArray(d.rows);
      setRows(r);
      setExpanded(new Set());
      setTotaux(d.totaux && typeof d.totaux === 'object' ? d.totaux : null);
      setPeriode(d.periode || null);
      setComputedAt(d.computed_at || '');
      setNote(d.note || '');
      const tot = d.total ?? d.count ?? d.total_rows ?? null;
      setTotalRows(Number.isFinite(Number(tot)) ? Number(tot) : null);
      // Listes de filtres : on conserve ce qu'on a déjà vu pour ne pas vider le select quand un filtre est actif.
      const pv = asArray(d.totaux?.par_vendeur).map((x) => x?.vendeur).filter(Boolean);
      setVendeursConnus((prev) => Array.from(new Set([...prev, ...pv, ...r.map((x) => x?.vendeur).filter(Boolean)])).sort());
      setMagasinsConnus((prev) => Array.from(new Set([...prev, ...r.map((x) => x?.magasin_id).filter(Boolean)])).sort());
    } catch (e) {
      if (!mountedRef.current) return;
      const msg = e.response?.status === 404
        ? t('adminPertes.endpointIndispo')
        : (e.response?.data?.detail || e.message || t('adminPertes.errLoad'));
      setError(typeof msg === 'string' ? msg : t('adminPertes.errLoad'));
      setRows([]);
      setTotaux(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [api, params]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const recalcul = async () => {
    if (!window.confirm(t('adminPertes.confirmRecalcul'))) return;
    setBusy('recalcul');
    try {
      const res = await api.post('/admin/pertes/recalcul');
      const d = res.data || {};
      const n = d.nb_ventes ?? d.ventes_analysees ?? d.count;
      toast.success(n !== undefined ? t('adminPertes.recalculDone', { count: nf(num(n)) }) : t('adminPertes.recalculDoneGeneric'));
      await load({ refresh: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('adminPertes.recalculFail'));
    } finally {
      if (mountedRef.current) setBusy('');
    }
  };

  const exportCsv = async () => {
    setBusy('export');
    try {
      const filters = { ...params };
      delete filters.limit;
      delete filters.offset;
      const res = await api.get('/admin/pertes/export.csv', { params: filters, responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pertes_${dateDebut}_${dateFin || isoDate(Date.now())}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(t('adminPertes.exportDone'));
    } catch (e) {
      toast.error(e.response?.status === 404 ? t('adminPertes.exportUnavailable') : (e.response?.data?.detail || t('adminPertes.exportFail')));
    } finally {
      if (mountedRef.current) setBusy('');
    }
  };

  /** Ouvre le ticket de caisse (PDF 80 mm) dans un nouvel onglet — simple visualisation, sans impression. */
  const openTicket = async (r) => {
    const ref = r?.ref_vente;
    if (!ref) { toast.error(t('adminPertes.refManquante')); return; }
    setTicketBusy(ref);
    try {
      await openTicketPdf(api, { type: 'vente', ref });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (e.message || t('adminPertes.ticketFail')));
    } finally {
      if (mountedRef.current) setTicketBusy('');
    }
  };

  const toggleRow = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const parType = totaux?.par_type && typeof totaux.par_type === 'object' ? totaux.par_type : {};
  // par_type peut être { type: montant } ou { type: { total, nb_ventes } } selon la version du backend.
  const typeTotal = (v) => (v && typeof v === 'object' ? num(v.total ?? v.perte ?? v.montant) : num(v));
  const perteHorsStock = typeTotal(parType.hors_stock_sous_prix);
  const perteRemise = typeTotal(parType.remise_excessive);
  const perteSousCatalogue = typeTotal(parType.prix_sous_catalogue);
  const perteGlobale = totaux?.global !== undefined ? num(totaux.global) : perteHorsStock + perteRemise + perteSousCatalogue;

  const parVendeur = useMemo(() => (
    asArray(totaux?.par_vendeur)
      .map((v) => ({
        vendeur: v?.vendeur || '—',
        hors_stock_sous_prix: num(v?.hors_stock_sous_prix),
        remise_excessive: num(v?.remise_excessive),
        prix_sous_catalogue: num(v?.prix_sous_catalogue),
        total: v?.total !== undefined ? num(v.total) : num(v?.hors_stock_sous_prix) + num(v?.remise_excessive) + num(v?.prix_sous_catalogue),
        nb_ventes: num(v?.nb_ventes),
      }))
      .sort((a, b) => b.total - a.total)
  ), [totaux]);

  const nbVentesConcernees = useMemo(() => {
    if (totaux?.nb_ventes !== undefined) return num(totaux.nb_ventes);
    if (parVendeur.length) return parVendeur.reduce((s, v) => s + v.nb_ventes, 0);
    return new Set(rows.map((r) => r?.ref_vente).filter(Boolean)).size;
  }, [totaux, parVendeur, rows]);

  const sortedRows = useMemo(() => {
    const arr = rows.map((r, i) => ({ ...r, _i: i }));
    arr.sort((a, b) => {
      const d = num(a.perte) - num(b.perte);
      if (d !== 0) return sortDir === 'desc' ? -d : d;
      return a._i - b._i;
    });
    return arr;
  }, [rows, sortDir]);

  const hasPrev = page > 0;
  const hasNext = totalRows !== null ? (page + 1) * PAGE_SIZE < totalRows : rows.length === PAGE_SIZE;
  const pageLabel = totalRows !== null
    ? t('adminPertes.pageRange', { from: Math.min(page * PAGE_SIZE + 1, totalRows), to: Math.min((page + 1) * PAGE_SIZE, totalRows), total: nf(totalRows) })
    : `${t('adminPertes.pageN', { page: page + 1 })}${rows.length ? ` · ${plural('lignes', rows.length)}` : ''}`;

  const SortIcon = sortDir === 'desc' ? ArrowDown : sortDir === 'asc' ? ArrowUp : ArrowUpDown;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6 fade-in" data-testid="admin-pertes">
        <PageHeader
          title={t('adminPertes.title')}
          subtitle={t('adminPertes.subtitle')}
          icon={TrendingDown}
          actions={(
            <>
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={!!busy || loading} className="border-gray-600 text-gray-300" data-testid="pertes-export-btn">
                {busy === 'export' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} {t('adminPertes.exportCsv')}
              </Button>
              <Button size="sm" onClick={recalcul} disabled={!!busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="pertes-recalcul-btn">
                {busy === 'recalcul' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} {t('adminPertes.recalcul')}
              </Button>
            </>
          )}
        />

        {/* Filtres */}
        <div className="glass p-4 rounded-2xl border border-white/10 space-y-3" data-testid="pertes-filters">
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => applyPreset(p.key)} className={staffChipClass(preset === p.key)} data-testid={`pertes-preset-${p.key}`}>
                {t(p.labelKey)}
              </button>
            ))}
            {periode && (
              <span className="text-xs text-gray-500 ml-auto">
                {t('adminPertes.periode', { debut: fmtDate(periode.date_debut), fin: fmtDate(periode.date_fin) })}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <label className="space-y-1">
              <span className="text-xs text-gray-500">{t('adminPertes.du')}</span>
              <input type="date" value={dateDebut} max={dateFin || undefined} disabled={preset !== 'custom'}
                onChange={(e) => { setDateDebut(e.target.value); setPage(0); }} className={`${staffInputClass} disabled:opacity-50`} data-testid="pertes-date-debut" />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-gray-500">{t('adminPertes.au')}</span>
              <input type="date" value={dateFin} min={dateDebut || undefined} disabled={preset !== 'custom'}
                onChange={(e) => { setDateFin(e.target.value); setPage(0); }} className={`${staffInputClass} disabled:opacity-50`} data-testid="pertes-date-fin" />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-gray-500">{t('adminPertes.vendeur')}</span>
              <select value={vendeur} onChange={(e) => { setVendeur(e.target.value); setPage(0); }} className={staffInputClass} data-testid="pertes-vendeur">
                <option value="">{t('adminPertes.tous')}</option>
                {vendeursConnus.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs text-gray-500">{t('adminPertes.type')}</span>
              <select value={type} onChange={(e) => { setType(e.target.value); setPage(0); }} className={staffInputClass} data-testid="pertes-type">
                <option value="">{t('adminPertes.tous')}</option>
                {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{t(m.labelKey)}</option>)}
              </select>
            </label>
            {magasinsConnus.length > 0 && (
              <label className="space-y-1">
                <span className="text-xs text-gray-500">{t('adminPertes.magasin')}</span>
                <select value={magasinId} onChange={(e) => { setMagasinId(e.target.value); setPage(0); }} className={staffInputClass} data-testid="pertes-magasin">
                  <option value="">{t('adminPertes.tous')}</option>
                  {magasinsConnus.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-300 bg-red-950/40 border border-red-500/30 rounded-xl px-3 py-2" data-testid="pertes-error">{error}</p>
        )}

        {/* Tuiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label={t('adminPertes.tileTotal')} value={eur(perteGlobale)} accent="text-red-400" hint={computedAt ? t('adminPertes.calculeLe', { date: fmtDateTime(computedAt) }) : undefined} testId="pertes-tile-total" />
          <Tile label={t('adminPertes.typeHorsStock')} value={eur(perteHorsStock)} accent="text-red-300" testId="pertes-tile-hors-stock" />
          <Tile label={t('adminPertes.remisesExcessives')} value={eur(perteRemise)} accent="text-amber-400" hint={t('adminPertes.auDelaDe', { pct: seuilPct })} testId="pertes-tile-remise" />
          <Tile label={t('adminPertes.tileNb')} value={nf(nbVentesConcernees)} testId="pertes-tile-nb" />
        </div>

        {note && (
          <p className="text-xs text-gray-500 flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{note}</span></p>
        )}

        {/* Par vendeur */}
        <div className="glass p-4 sm:p-5 rounded-2xl border border-white/10 space-y-3" data-testid="pertes-par-vendeur">
          <h2 className="text-white font-bold">{t('adminPertes.parVendeur')}</h2>
          {loading && !parVendeur.length ? (
            <div className="text-center text-gray-400 py-6">{t('adminPertes.loading')}</div>
          ) : parVendeur.length === 0 ? (
            <p className="text-sm text-gray-500">{t('adminPertes.aucunePerte')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm tabular-nums">
                <thead className="text-gray-400 border-b border-white/10 text-xs">
                  <tr>
                    <th className="text-left py-2 pr-3">{t('adminPertes.vendeur')}</th>
                    <th className="text-right py-2 px-3">{t('adminPertes.typeHorsStock')}</th>
                    <th className="text-right py-2 px-3">{t('adminPertes.remisesExcessives')}</th>
                    <th className="text-right py-2 px-3">{t('adminPertes.colTotal')}</th>
                    <th className="text-right py-2 pl-3">{t('adminPertes.colNbVentes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {parVendeur.map((v) => (
                    <tr key={v.vendeur} className="border-b border-white/5 hover:bg-white/[0.03]">
                      <td className="py-2 pr-3 text-white font-medium">
                        <button type="button" onClick={() => { setVendeur(v.vendeur === vendeur ? '' : v.vendeur); setPage(0); }} className="hover:underline text-left" title={t('adminPertes.filtrerVendeur')}>
                          {v.vendeur}
                        </button>
                      </td>
                      <td className="text-right px-3 text-red-300">{eur(v.hors_stock_sous_prix)}</td>
                      <td className="text-right px-3 text-amber-400">{eur(v.remise_excessive)}</td>
                      <td className="text-right px-3 text-white font-semibold">{eur(v.total)}</td>
                      <td className="text-right pl-3 text-gray-300">{nf(v.nb_ventes)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/10 text-gray-300">
                    <td className="py-2 pr-3 font-semibold">{t('adminPertes.colTotal')}</td>
                    <td className="text-right px-3">{eur(parVendeur.reduce((s, v) => s + v.hors_stock_sous_prix, 0))}</td>
                    <td className="text-right px-3">{eur(parVendeur.reduce((s, v) => s + v.remise_excessive, 0))}</td>
                    <td className="text-right px-3 text-white font-bold">{eur(parVendeur.reduce((s, v) => s + v.total, 0))}</td>
                    <td className="text-right pl-3">{nf(parVendeur.reduce((s, v) => s + v.nb_ventes, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Détail */}
        <div className="glass p-4 sm:p-5 rounded-2xl border border-white/10 space-y-3" data-testid="pertes-detail">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-white font-bold">{t('adminPertes.detailTitle')}</h2>
            <span className="text-xs text-gray-500">{pageLabel}</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : sortedRows.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">{t('adminPertes.aucuneLigne')}</p>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full text-xs sm:text-sm tabular-nums min-w-[1040px]">
                <thead className="text-gray-400 border-b border-white/10 text-xs">
                  <tr>
                    <th className="text-left py-2 pr-2">{t('adminPertes.colDate')}</th>
                    <th className="text-left py-2 px-2">{t('adminPertes.colTicket')}</th>
                    <th className="text-left py-2 px-2">{t('adminPertes.vendeur')}</th>
                    <th className="text-left py-2 px-2">{t('adminPertes.type')}</th>
                    <th className="text-left py-2 px-2">{t('adminPertes.colArticle')}</th>
                    <th className="text-right py-2 px-2">{t('adminPertes.colQte')}</th>
                    <th className="text-right py-2 px-2">{t('adminPertes.colPrixVendu')}</th>
                    <th className="text-right py-2 px-2">{t('adminPertes.colPrixRef')}</th>
                    <th className="text-right py-2 px-2">{t('adminPertes.colRemise')}</th>
                    <th className="text-right py-2 px-2">
                      <button type="button" onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))} className="inline-flex items-center gap-1 hover:text-white" title={t('adminPertes.trierPerte')} data-testid="pertes-sort-perte">
                        {t('adminPertes.colPerte')} <SortIcon className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-left py-2 px-2">{t('adminPertes.colStatut')}</th>
                    <th className="text-right py-2 pl-2">{t('adminPertes.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r, i) => {
                    const rowKey = `${r.ref_vente || 'v'}-${r.produit_ref || ''}-${r.type || ''}-${r._i}`;
                    const isOpen = expanded.has(rowKey);
                    const articles = asArray(r.articles);
                    const nbArticles = articles.reduce((s, a) => s + (num(a?.quantite) || 1), 0);
                    return (
                      <React.Fragment key={rowKey}>
                    <tr className={`border-b border-white/5 hover:bg-white/[0.03] ${isOpen ? 'bg-white/[0.04]' : ''}`} data-testid={`pertes-row-${i}`}>
                      <td className="py-2 pr-2 text-gray-300 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-2 font-mono text-white whitespace-nowrap">
                        <span title={ticketTooltip(r)} className={articles.length ? 'cursor-help border-b border-dotted border-gray-500' : ''}>{r.ref_vente || '—'}</span>
                      </td>
                      <td className="px-2 text-gray-300 whitespace-nowrap">{r.vendeur || '—'}{r.magasin_id ? <span className="text-gray-600"> · {r.magasin_id}</span> : null}</td>
                      <td className="px-2"><TypeBadge type={r.type} /></td>
                      <td className="px-2 text-gray-200 max-w-[260px]">
                        <span className="block truncate" title={r.designation || ''}>{r.designation || '—'}</span>
                        {r.produit_ref && <span className="block text-[10px] text-gray-500 font-mono truncate">{r.produit_ref}</span>}
                      </td>
                      <td className="text-right px-2 text-gray-300">{nf(num(r.quantite) || 1)}</td>
                      <td className="text-right px-2 text-white">{eur(r.prix_vendu)}</td>
                      <td className="text-right px-2 text-gray-300">{eur(r.prix_reference)}</td>
                      <td className={`text-right px-2 ${num(r.remise_pct) > REMISE_SEUIL_PCT ? 'text-amber-400' : 'text-gray-300'}`}>{pct(r.remise_pct)}</td>
                      <td className="text-right px-2 text-red-400 font-semibold">{eur(r.perte)}</td>
                      <td className={`px-2 whitespace-nowrap ${statutClass(r.statut)}`}>
                        {r.statut || '—'}
                        {r.source && <span className="block text-[10px] text-gray-600">{r.source}</span>}
                      </td>
                      <td className="pl-2 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button type="button" onClick={() => openTicket(r)} disabled={!r.ref_vente || ticketBusy === r.ref_vente}
                            className="inline-flex items-center gap-1 min-h-[28px] px-2 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-40 text-gray-100 text-[11px] font-semibold"
                            title={t('adminPertes.ouvrirTicket')} data-testid={`pertes-row-${i}-ticket`}>
                            {ticketBusy === r.ref_vente ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} {t('adminPertes.ticket')}
                          </button>
                          <button type="button" onClick={() => toggleRow(rowKey)} aria-expanded={isOpen}
                            className={`inline-flex items-center gap-1 min-h-[28px] px-2 rounded-lg hover:bg-white/10 text-[11px] font-semibold ${isOpen ? 'text-white' : 'text-gray-300 hover:text-white'}`}
                            title={isOpen ? t('adminPertes.masquerVente') : t('adminPertes.voirVenteTitle')} data-testid={`pertes-row-${i}-toggle`}>
                            {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {t('adminPertes.voirVente')}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-white/5 bg-white/[0.02]" data-testid={`pertes-row-${i}-vente`}>
                        <td colSpan={12} className="px-2 py-3">
                          <div className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-2 max-w-3xl">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 text-xs">
                              <span className="text-gray-400">
                                {t('adminPertes.venteLabel')} <span className="font-mono text-white">{r.ref_vente || '—'}</span> · {fmtDate(r.date)}
                                {r.vendeur ? <span> · {r.vendeur}</span> : null}
                              </span>
                              <span className="text-gray-400">{t('adminPertes.paiement')} <span className="text-gray-200 font-medium">{modePaiementLabel(r.mode_paiement)}</span></span>
                            </div>
                            {articles.length === 0 ? (
                              <p className="text-xs text-gray-500">{t('adminPertes.contenuIndispo')}</p>
                            ) : (
                              <ul className="divide-y divide-white/5">
                                {articles.map((a, j) => {
                                  const isLigne = a?.produit_ref && r.produit_ref && String(a.produit_ref) === String(r.produit_ref);
                                  return (
                                    <li key={`${a?.produit_ref || 'a'}-${j}`} className="py-1.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-3 text-xs">
                                      <div className="min-w-0">
                                        <span className={`block truncate ${isLigne ? 'text-white font-medium' : 'text-gray-200'}`} title={a?.designation || ''}>
                                          {a?.designation || '—'}
                                          {isLigne && <span className="ml-1 text-[10px] text-red-400 font-semibold">{t('adminPertes.ligneEnPerte')}</span>}
                                        </span>
                                        {a?.produit_ref && <span className="block text-[10px] text-gray-500 font-mono truncate">{a.produit_ref}</span>}
                                      </div>
                                      <div className="flex items-center gap-3 shrink-0 tabular-nums">
                                        <span className="text-gray-400">{nf(num(a?.quantite) || 1)} × {eur(a?.prix_unitaire)}</span>
                                        <span className="text-white font-semibold min-w-[84px] text-right">{eur(ligneTotal(a))}</span>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 pt-2 border-t border-white/10 text-xs">
                              <span className="text-gray-400">
                                {plural('lignes', articles.length)}{articles.length ? ` · ${plural('articles', nbArticles)}` : ''}
                              </span>
                              <span className="text-gray-300">{t('adminPertes.totalVente')} <span className="text-white font-bold tabular-nums">{eur(r.total_vente)}</span></span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
            <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={!hasPrev || loading}
              className="min-h-[36px] px-3 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-40 text-gray-100 text-xs font-semibold flex items-center gap-1" data-testid="pertes-prev">
              <ChevronLeft className="w-4 h-4" /> {t('adminPertes.precedent')}
            </button>
            <span className="text-xs text-gray-500">{t('adminPertes.lignesParPage', { count: PAGE_SIZE })}</span>
            <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!hasNext || loading}
              className="min-h-[36px] px-3 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-40 text-gray-100 text-xs font-semibold flex items-center gap-1" data-testid="pertes-next">
              {t('adminPertes.suivant')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Légende */}
        <div className="glass p-4 sm:p-5 rounded-2xl border border-white/10 text-sm text-gray-400 space-y-2" data-testid="pertes-legende">
          <h2 className="text-white font-bold flex items-center gap-2"><Info className="w-4 h-4 text-gray-400" /> {t('adminPertes.legendeTitle')}</h2>
          <ul className="space-y-1.5 list-disc pl-5">
            <li>
              <TypeBadge type="hors_stock_sous_prix" /> <span className="ml-1">{t('adminPertes.legendeHorsStockText')} <span className="text-gray-200 tabular-nums">{t('adminPertes.legendeHorsStockFormula')}</span>.</span>
            </li>
            <li>
              <TypeBadge type="remise_excessive" /> <span className="ml-1">{t('adminPertes.legendeRemiseText', { seuil: seuilPct })} <span className="text-gray-200 tabular-nums">{t('adminPertes.legendeRemiseFormula', { seuil: seuilPct })}</span>.</span>
            </li>
            <li>
              <TypeBadge type="prix_sous_catalogue" /> <span className="ml-1">{t('adminPertes.legendeSousCatalogue')}</span>
            </li>
          </ul>
          <p className="text-xs text-gray-500">
            {t('adminPertes.legendeFooter')}
          </p>
        </div>
      </div>
    </Layout>
  );
}
